var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import "vue";
class VueComponent {
  // This will allow us to pass the vue proxy inside the component
  constructor() {
    return new Proxy(this, {
      get: (object, key, proxy) => {
        return this.__getter(object, key, proxy);
      }
    });
  }
  __getter(object, key, proxy) {
    return object[key];
  }
}
const $internalHooks = [
  "data",
  "beforeCreate",
  "created",
  "beforeMount",
  "mounted",
  "beforeUnmount",
  "unmounted",
  "beforeUpdate",
  "updated",
  "activated",
  "deactivated",
  "render",
  "errorCaptured",
  // 2.5
  "serverPrefetch"
  // 2.6
];
function createDecorator(factory) {
  return (target, key, index) => {
    const Ctor = typeof target === "function" ? target : target.constructor;
    if (!Ctor.__decorators__) {
      Ctor.__decorators__ = [];
    }
    if (typeof index !== "number") {
      index = void 0;
    }
    Ctor.__decorators__.push((options) => factory(options, key, index));
  };
}
function buildComponent(OriginalClass, decoratorOptions) {
  var _a;
  const mixins = ((_a = OriginalClass.__mixins) == null ? void 0 : _a.slice()) ?? [];
  if (decoratorOptions && decoratorOptions.mixins) {
    mixins.push(...decoratorOptions.mixins);
    delete decoratorOptions.mixins;
  }
  if (decoratorOptions && decoratorOptions.data) {
    mixins.push({
      data: decoratorOptions.data
    });
    delete decoratorOptions.data;
  }
  if (decoratorOptions && decoratorOptions.props) {
    mixins.push({
      props: decoratorOptions.props
    });
    delete decoratorOptions.props;
  }
  const options = {
    ...decoratorOptions ?? {},
    name: (decoratorOptions == null ? void 0 : decoratorOptions.name) || OriginalClass._componentTag || OriginalClass.name,
    mixins
  };
  const decorators = OriginalClass.__decorators__;
  if (decorators) {
    decorators.forEach((fn) => fn(options));
    delete OriginalClass.__decorators__;
  }
  options.data = function() {
    let vm = this;
    if (!OriginalClass.prototype.__getter) {
      throw new Error("Component " + options.name + " should either extend VueComponent or extend Mixins.");
    }
    OriginalClass.prototype.__getter = function(object, key, proxy) {
      const v = Reflect.get(object, key, proxy);
      if (v === void 0) {
        return vm[key];
      }
      return v;
    };
    const defaultData = {};
    const instance = new OriginalClass();
    vm = {};
    for (const key of Object.getOwnPropertyNames(instance)) {
      if (instance[key] !== void 0) {
        if (options.props && key in options.props) {
          console.error("Setting the default property value via normal properties is not supported. Please use @Prop({default: 123})", { component: options.name, key, value: instance[key] });
        } else {
          defaultData[key] = instance[key];
        }
      }
    }
    return defaultData;
  };
  const proto = OriginalClass.prototype;
  Object.getOwnPropertyNames(proto).forEach(function(key) {
    if (key === "constructor") {
      return;
    }
    if ($internalHooks.indexOf(key) > -1) {
      options[key] = proto[key];
      return;
    }
    const descriptor = Object.getOwnPropertyDescriptor(proto, key);
    if (descriptor.value !== void 0) {
      if (typeof descriptor.value === "function") {
        (options.methods || (options.methods = {}))[key] = descriptor.value;
      } else {
        (options.mixins || (options.mixins = [])).push({
          data() {
            return { [key]: descriptor.value };
          }
        });
      }
    } else if (descriptor.get || descriptor.set) {
      (options.computed || (options.computed = {}))[key] = {
        get: descriptor.get,
        set: descriptor.set
      };
    }
  });
  return options;
}
function Component(options) {
  if (typeof options === "function") {
    return buildComponent(options);
  }
  return (OriginalClass) => buildComponent(OriginalClass, options);
}
function Mixins(...mixins) {
  var _a;
  return _a = class extends VueComponent {
  }, __publicField(_a, "__mixins", mixins), _a;
}
function injectPropertyDecorator(target, key, options) {
  createDecorator((componentOptions, k) => {
    if (!componentOptions.props) {
      componentOptions.props = {};
    }
    componentOptions.props[k] = options ?? {};
  })(target, key);
}
function Prop(optionsOrTarget, key) {
  if (key) {
    return injectPropertyDecorator(optionsOrTarget, key, {});
  }
  return (target, key2) => {
    return injectPropertyDecorator(target, key2, optionsOrTarget);
  };
}
function Ref(refKey) {
  return createDecorator((options, key) => {
    options.computed = options.computed || {};
    options.computed[key] = {
      cache: false,
      get() {
        return this.$refs[refKey || key];
      }
    };
  });
}
function Watch(path, watchOptions = {}) {
  return createDecorator((componentOptions, handler) => {
    componentOptions.watch || (componentOptions.watch = /* @__PURE__ */ Object.create(null));
    const watch = componentOptions.watch;
    if (typeof watch[path] === "object" && !Array.isArray(watch[path])) {
      watch[path] = [watch[path]];
    } else if (typeof watch[path] === "undefined") {
      watch[path] = [];
    }
    watch[path].push({ handler, ...watchOptions });
  });
}
class Vue extends VueComponent {
  constructor() {
    super();
    console.warn("Vue constructor is still used. Please replace all extends Vue from code with extends VueComponent");
  }
}
export {
  Component,
  Mixins,
  Prop,
  Ref,
  Vue,
  VueComponent,
  Watch
};
